<template>
  <Card
    v-if="hasUsers"
    :class="$style.card"
    data-test-id="UserNotification__widgetCard"
  >
    <Count
      :count="total"
      countSize="medium"
      :hasDivider="false"
      label="User Notifications"
    />
    <template v-slot:body>
      <ParagraphBody>
        <WidgetList
          :list="metrics"
          @handleClickAction="navigateToUsersList"
        />
      </ParagraphBody>
    </template>
  </Card>
  <!-- Incase of no data -->
  <NoDataWidgetCard
    v-else
    :widgetInfo="$options.noUserWidgetData"
    :widgetUrl="$options.userRoutes"
  />
</template>
<script>
import {
  Card,
  Count,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts, ItemTypeRoutes, StoreModuleMap, UsersConsts,
} from 'client/util/Constants';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import HomeWidgetConsts from 'client/HomePageV2/constants/HomeWidgetConsts';
import NoDataWidgetCard from 'client/HomePageV2/widgets/NoDataWidgetCard';
import SegmentEventMixin from 'client/HomePageV2/mixins/SegmentEventMixin';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import SystemUserFilters from 'client/components/ItemTable/CollectionFilterComponents/constants/SystemUserFilters';
import Util from 'client/util/Util';
import WidgetList from 'client/HomePageV2/widgets/WidgetList';

const userDisplayPreferencesModule = StoreModuleMap[ItemTypeConsts.userDisplayPreferences];

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const userRoutes = ItemTypeRoutes[ItemTypeConsts.systemUser];
const { widgetTypes, userNotificationMetrics, userNotificationMetricsNames } = HomeWidgetConsts;
const noUserWidgetData = UsersConsts.NoUserWidgetText;
const systemUserCollection = StoreModuleMap[ItemTypeConsts.systemUser];
const organizationsCollection = StoreModuleMap[ItemTypeConsts.organizations];

export default {
  name: 'UserNotificationsWidget',

  components: {
    Card,
    Count,
    NoDataWidgetCard,
    ParagraphBody,
    WidgetList,
  },

  userRoutes,
  noUserWidgetData,

  mixins: [SegmentEventMixin],

  data() {
    return {
      isLoading: true,
      notifications: {
        usersWithoutPassword: 0,
        usersWithAdminSudoAccess: 0,
        userWithAdminSudoPasswordlessAccess: 0,
        usersWithSambaAccess: 0,
      },
      total: 0,
      widgetType: widgetTypes.userNotifications,
      userNotificationMetricsNames,
    };
  },

  computed: {
    ...mapState(userDisplayPreferencesModule, {
      displaySettings: state => state.displayPreferencesData,
    }),
    ...mapState(settingsModule, ['orgId']),

    ...mapGetters(organizationsCollection, ['getItemDetails']),

    displayPreferences() {
      return this.displaySettings.display.userNotifications;
    },

    filters() {
      return {
        usersWithoutPassword: SystemUserFilters.noPassword(true),
        usersWithAdminSudoAccess: SystemUserFilters.sudo(true),
        userWithAdminSudoPasswordlessAccess: SystemUserFilters.sudoPasswordLess(true),
        usersWithSambaAccess: SystemUserFilters.sambaAccess(true),
      };
    },

    hasUsers() {
      return get(this.getItemDetails(this.orgId), 'systemUsersCount', 0);
    },

    metrics() {
      const notificationList = {
        usersWithoutPassword: {
          name: this.userNotificationMetricsNames.usersWithoutPassword,
          value: this.notifications.usersWithoutPassword,
          filter: 'usersWithoutPassword',
        },
        usersWithAdminSudoAccess: {
          name: this.userNotificationMetricsNames.usersWithAdminSudoAccess,
          value: this.notifications.usersWithAdminSudoAccess,
          filter: 'usersWithAdminSudoAccess',
        },
        userWithAdminSudoPasswordlessAccess: {
          name: this.userNotificationMetricsNames.userWithAdminSudoPasswordlessAccess,
          value: this.notifications.userWithAdminSudoPasswordlessAccess,
          filter: 'userWithAdminSudoPasswordlessAccess',
        },
        usersWithSambaAccess: {
          name: this.userNotificationMetricsNames.usersWithSambaAccess,
          value: this.notifications.usersWithSambaAccess,
          filter: 'usersWithSambaAccess',
        },
      };

      // Add each notification to the widget if it is displayed
      return Object.entries(this.displayPreferences).reduce((allMetrics, [key, metric]) => {
        const { visible } = metric;
        const notification = notificationList[key];
        if (notification && visible) {
          allMetrics.push(notification);
        }
        return allMetrics;
      }, []);
    },
  },

  created() {
    const noPassword = this.displayPreferences.usersWithoutPassword.visible;
    const adminSudo = this.displayPreferences.usersWithAdminSudoAccess.visible;
    const sudoPasswordless = this.displayPreferences.userWithAdminSudoPasswordlessAccess.visible;
    const samba = this.displayPreferences.usersWithSambaAccess.visible;
    const userMetricsToFetch = [
      ...(adminSudo ? [this.fetchUsersData(userNotificationMetrics.usersWithAdminSudoAccess)] : []),
      ...(noPassword ? [this.fetchUsersData(userNotificationMetrics.usersWithoutPassword)] : []),
      ...(samba ? [this.fetchUsersData(userNotificationMetrics.usersWithSambaAccess)] : []),
      ...(sudoPasswordless
        ? [this.fetchUsersData(userNotificationMetrics.userWithAdminSudoPasswordlessAccess)]
        : []),
    ];
    Promise.allSettled(userMetricsToFetch).finally(() => {
      this.isLoading = false;
    });
  },

  methods: {
    ...mapActions(systemUserCollection, {
      fetchUsers: 'fetchCollection',
      setFilter: 'setFilter',
    }),

    async fetchUsersData(type) {
      const { totalCount } = await this.fetchUsersWithFilter(this.filters[type]);
      if (totalCount) {
        this.notifications[type] = totalCount;
        this.total += this.notifications[type];
      }
      return Promise.resolve();
    },

    async fetchUsersWithFilter(filter) {
      this.isLoading = true;
      let users;
      try {
        users = await SystemUserApi.fetch(null, null, null, null, null, [filter]);
      } catch (err) {
        users = {};
      }
      return users;
    },

    async navigateToUsersList(item) {
      this.triggerLinkClickedHandler(
        'User notification metric link',
        'User notifications widget',
        this.$options.userRoutes,
        item.name,
      );
      this.setFilter({ filter: this.filters[item.filter] });
      Util.redirect(this.$options.userRoutes);
    },
  },
};
</script>
<style module scoped>
@value (cardShadow1) from '~css/shadows.css';

.card {
  box-shadow: cardShadow1;
}
</style>
